import { useTranslation } from 'react-i18next';
import { AirlineStatus } from '@/api/generated/schemas';
import { CurrencySelectorField } from '@/modules/codebooks';
import {
  OrganizationAddressesPanel,
  OrganizationContactsPanel,
  OrganizationSelectorField,
} from '@/modules/general';
import { SegmentedField, TextField, TextareaField } from '@/shared/forms';
import { useMagicFormEdit } from '@/shared/legacyMagicBridge';
import { Button, Panel } from '@/shared/ui';
import type { AirlineFormApi } from '../useAirlineForm';

const LABEL_W = 168;

interface AirlineGeneralTabProps {
  form: AirlineFormApi;
}

/**
 * Záložka General — panel základních údajů dopravce, read-only panely adresáře napojené organizace
 * a interní poznámka. Panely vlastní modul `General`; plní se podle organizace **vybrané ve
 * formuláři**, ne podle uložené letecké společnosti, takže se vyplní hned po výběru i po přehození
 * a bez ukládání.
 */
export function AirlineGeneralTab({ form }: AirlineGeneralTabProps) {
  const { t } = useTranslation('inventory');
  // Proklik do organizace patří do stacku okna, ve kterém detail běží (osamostatněné okno ≠ plocha).
  const openFormEdit = useMagicFormEdit();

  const statusOptions = [
    { value: AirlineStatus.Active, label: t('airlineStatus.Active') },
    { value: AirlineStatus.Inactive, label: t('airlineStatus.Inactive') },
  ];

  return (
    <div className="gap-phx-3 flex flex-col">
      <Panel variant="filled" icon="plane" title={t('airline.sections.details')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="organization">
            {(field) => (
              <div className="gap-phx-2 flex items-start">
                <div className="min-w-0 flex-1">
                  <OrganizationSelectorField
                    field={field}
                    mode="single"
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('airline.organization.label')}
                    required
                    placeholder={t('airline.organization.placeholder')}
                    testId="airline-organization"
                  />
                </div>
                {/* Proklik do legacy formuláře organizace; bez vybrané organizace neaktivní. */}
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  iconLeft="external-link"
                  title={t('airline.organization.openDetail')}
                  aria-label={t('airline.organization.openDetail')}
                  disabled={field.state.value == null}
                  data-testid="airline-organization-open"
                  onClick={() => {
                    const partyId = field.state.value?.partyId;
                    if (partyId != null) openFormEdit('Organization', partyId);
                  }}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="tradeName">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.tradeName.label')}
                required
                placeholder={t('airline.tradeName.placeholder')}
                data-testid="airline-trade-name"
              />
            )}
          </form.Field>

          <form.Field name="iata">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.iata.label')}
                maxLength={2}
                helperText={t('airline.iata.hint')}
                data-testid="airline-iata"
              />
            )}
          </form.Field>

          <form.Field name="icao">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.icao.label')}
                maxLength={3}
                helperText={t('airline.icao.hint')}
                data-testid="airline-icao"
              />
            )}
          </form.Field>

          <form.Field name="callsign">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.callsign.label')}
                data-testid="airline-callsign"
              />
            )}
          </form.Field>

          <form.Field name="web">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.web.label')}
                placeholder={t('airline.web.placeholder')}
                data-testid="airline-web"
              />
            )}
          </form.Field>

          <form.Field name="currency">
            {(field) => (
              <CurrencySelectorField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.currency.label')}
                placeholder={t('airline.currency.placeholder')}
                testId="airline-currency"
              />
            )}
          </form.Field>

          <form.Field name="status">
            {(field) => (
              <SegmentedField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.status.label')}
                required
                options={statusOptions}
                testId="airline-status"
              />
            )}
          </form.Field>
        </div>
      </Panel>

      <form.Subscribe selector={(state) => state.values.organization?.partyId ?? null}>
        {(partyId) => (
          <>
            <OrganizationAddressesPanel partyId={partyId} />
            <OrganizationContactsPanel partyId={partyId} />
          </>
        )}
      </form.Subscribe>

      <Panel variant="filled" icon="doc" title={t('airline.sections.note')}>
        <form.Field name="note">
          {(field) => (
            <TextareaField field={field} layout="stacked" data-testid="airline-note" rows={4} />
          )}
        </form.Field>
      </Panel>
    </div>
  );
}
